import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { rpc } from '../../api/rpc'
import type { Connector } from '../../api/types'
import { cn } from '../../lib/cn'
import { IconCheck, IconPlus } from '../icons'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'
import { useToast } from '../ui/Toast'
import { FigmaTokenForm, GithubConnectForm, JiraConnectForm, SocialTokenForm, TelegramAddForm, ZimbraLoginForm } from './forms'
import { McpServerForm, type McpPreset } from './McpForm'

/**
 * "Discover" tab of Settings → Connectors (docs/web-ui.md §3): one card per thing you can connect,
 * laid out like Claude's connector directory. Each Google product is its own card (they share one
 * Google sign-in underneath), and MCP has a card for a remote server, one for a local (sandbox)
 * server and a few well-known remote servers with the URL filled in.
 */

export const GOOGLE_PRODUCTS = ['drive', 'gmail', 'calendar', 'sheets', 'docs'] as const
export type GoogleProduct = typeof GOOGLE_PRODUCTS[number]

type ItemId = GoogleProduct | 'telegram' | 'social' | 'figma' | 'zimbra' | 'jira' | 'mcp-remote' | 'mcp-local' | 'notion' | 'linear' | 'github' | 'sentry' | 'figma-mcp'

interface CatalogItem {
  id: ItemId
  name: string
  by: string
  section: 'builtin' | 'mcp'
  icon: ReactNode
  tile: string
  verified?: boolean
  product?: GoogleProduct
  preset?: McpPreset
}

const svg = (paths: ReactNode) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths}</svg>
)
const GLYPH = {
  drive: svg(<><path d="M8 4h8l5 9-4 7H7l-4-7z" /><path d="M8 4l5 9h8M3 13h10l-3 7" /></>),
  gmail: svg(<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></>),
  calendar: svg(<><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /><path d="M8 14h3v3H8z" /></>),
  sheets: svg(<><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M4 9h16M4 15h16M10 9v12" /></>),
  docs: svg(<><path d="M6 3h9l4 4v14H6z" /><path d="M15 3v4h4M9 12h7M9 16h7" /></>),
  telegram: svg(<path d="M21 4L3 11l6 2 2 6 3-4 5 4z" />),
  social: svg(<><rect x="3" y="4" width="18" height="16" rx="3" /><path d="M3 9h18M8 14h5" /></>),
  remote: svg(<><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3.5 3 14.5 0 18M12 3c-3 3.5-3 14.5 0 18" /></>),
  figma: svg(<><path d="M9 3h3v6H9a3 3 0 0 1 0-6z" /><path d="M12 3h3a3 3 0 0 1 0 6h-3z" /><path d="M9 9h3v6H9a3 3 0 0 1 0-6z" /><circle cx="15" cy="12" r="3" /><path d="M9 15h3v3a3 3 0 1 1-3-3z" /></>),
  zimbra: svg(<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /><path d="M16 15h3" /></>),
  jira: svg(<><path d="M11.5 2.5v7.8a4.2 4.2 0 0 0 8.4 0V2.5h-8.4z" /><path d="M3.1 11.2V19a4.2 4.2 0 0 0 8.4 0v-7.8H3.1z" /></>),
  github: svg(<><path d="M9 19c-4 1.2-4-2.2-5.5-2.7M15 21v-3.4a3 3 0 0 0-.9-2.4c3-.3 6-1.5 6-6.6a5 5 0 0 0-1.4-3.5 4.6 4.6 0 0 0-.1-3.5s-1.1-.3-3.6 1.4a12.4 12.4 0 0 0-6.4 0C6.1 1.3 5 1.6 5 1.6a4.6 4.6 0 0 0-.1 3.5A5 5 0 0 0 3.5 8.6c0 5 3 6.3 6 6.6a3 3 0 0 0-.9 2.3V21" /></>),
  local: svg(<><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 9l3 3-3 3M12 15h5" /></>)
}
const letter = (ch: string) => <span className="font-display text-lg font-bold">{ch}</span>

function catalog(t: (k: string) => string): CatalogItem[] {
  const google = (product: GoogleProduct, name: string, tile: string): CatalogItem =>
    ({ id: product, name, by: 'Google', section: 'builtin', icon: GLYPH[product], tile, verified: true, product })
  const preset = (id: ItemId, name: string, by: string, url: string, auth: McpPreset['auth'], icon: ReactNode): CatalogItem =>
    ({ id, name, by, section: 'mcp', icon, tile: 'bg-ink-100 text-cream-50', preset: { name, url, auth } })
  return [
    google('drive', 'Google Drive', 'bg-mint-50 text-mint-500'),
    google('gmail', 'Gmail', 'bg-red-50 text-red-700'),
    google('calendar', 'Google Calendar', 'bg-ginger-100 text-ginger-600'),
    google('sheets', 'Google Sheets', 'bg-mint-50 text-mint-500'),
    google('docs', 'Google Docs', 'bg-ginger-100 text-ginger-600'),
    { id: 'telegram', name: 'Telegram', by: 'Telegram', section: 'builtin', icon: GLYPH.telegram, tile: 'bg-ginger-50 text-ginger-700', verified: true },
    { id: 'social', name: 'Meow Social', by: 'Meow', section: 'builtin', icon: GLYPH.social, tile: 'bg-amber-50 text-amber-700', verified: true },
    { id: 'figma', name: 'Figma', by: 'Figma', section: 'builtin', icon: GLYPH.figma, tile: 'bg-ginger-100 text-ginger-600', verified: true },
    { id: 'zimbra', name: 'Zimbra Mail', by: 'Zimbra', section: 'builtin', icon: GLYPH.zimbra, tile: 'bg-ginger-50 text-ginger-700', verified: true },
    { id: 'jira', name: 'Jira', by: 'Atlassian', section: 'builtin', icon: GLYPH.jira, tile: 'bg-ginger-50 text-ginger-700', verified: true },
    { id: 'github', name: 'GitHub', by: 'GitHub', section: 'builtin', icon: GLYPH.github, tile: 'bg-ink-100 text-cream-50', verified: true },
    { id: 'mcp-remote', name: t('connectors.catalog.mcpRemote'), by: t('connectors.catalog.byYou'), section: 'mcp', icon: GLYPH.remote, tile: 'bg-cream-200 text-ink-200' },
    { id: 'mcp-local', name: t('connectors.catalog.mcpLocal'), by: t('connectors.catalog.byYou'), section: 'mcp', icon: GLYPH.local, tile: 'bg-cream-200 text-ink-200' },
    preset('notion', 'Notion', 'Notion', 'https://mcp.notion.com/mcp', 'oauth', letter('N')),
    preset('linear', 'Linear', 'Linear', 'https://mcp.linear.app/mcp', 'oauth', letter('L')),
    preset('sentry', 'Sentry', 'Sentry', 'https://mcp.sentry.dev/mcp', 'oauth', letter('S')),
    preset('figma-mcp', 'Figma MCP', 'Figma', 'https://mcp.figma.com/mcp', 'oauth', GLYPH.figma)
  ]
}

const asList = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [])

export interface CatalogStatus { google: boolean; social: boolean; zimbra: boolean; sandbox: boolean; githubApp: string | null }

/** The connector a card already corresponds to, if any (opens its detail instead of adding again). */
function existing(item: CatalogItem, items: Connector[]): Connector | undefined {
  if (item.product) return items.find(c => c.kind === 'google' && asList(c.config.products).includes(item.product!))
  if (item.id === 'telegram') return items.find(c => c.kind === 'telegram')
  if (item.id === 'social') return items.find(c => c.kind === 'social')
  if (item.id === 'figma') return items.find(c => c.kind === 'figma')
  if (item.id === 'zimbra') return items.find(c => c.kind === 'zimbra')
  if (item.id === 'jira') return items.find(c => c.kind === 'jira')
  if (item.preset) return items.find(c => c.kind === 'mcp' && c.config.url === item.preset!.url)
  return undefined
}

function CatalogCard({ item, connected, unavailable, onAdd, onOpen }: {
  item: CatalogItem; connected?: Connector; unavailable?: string; onAdd(): void; onOpen(id: string): void
}) {
  const { t } = useTranslation()
  const act = () => (connected ? onOpen(connected.id) : unavailable ? undefined : onAdd())
  return (
    <article
      className={cn('group relative flex gap-3.5 rounded-2xl border border-line bg-cream-50 p-4 transition-colors',
        unavailable ? 'opacity-70' : 'cursor-pointer hover:border-cream-400')}
      onClick={act}
    >
      <div className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-xl', item.tile)}>{item.icon}</div>
      <div className="min-w-0 flex-1 pr-10">
        <h3 className="flex items-center gap-1.5 font-semibold text-ink-100">
          <span className="truncate">{item.name}</span>
          {item.verified && (
            <span className="text-ink-500" title={t('connectors.catalog.builtIn')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-label={t('connectors.catalog.builtIn')}>
                <circle cx="12" cy="12" r="9" /><path d="M8 12.5l2.5 2.5L16 9.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          )}
        </h3>
        <p className="mt-0.5 line-clamp-2 text-[13.5px] leading-snug text-ink-400">{t(`connectors.catalog.desc_${item.id.replace('-', '_')}`)}</p>
        <p className="mt-1 text-xs text-ink-500">
          {unavailable ?? (connected ? t('connectors.status_connected') : t('connectors.catalog.by', { name: item.by }))}
        </p>
      </div>
      <div className="absolute right-3 top-3">
        {connected ? (
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-mint-50 text-mint-500" aria-label={t('connectors.status_connected')}>
            <IconCheck width={16} height={16} />
          </span>
        ) : (
          <Button
            variant="outline" size="icon-sm" disabled={Boolean(unavailable)}
            aria-label={t('connectors.catalog.connectTo', { name: item.name })}
            onClick={e => { e.stopPropagation(); onAdd() }}
          >
            <IconPlus width={16} height={16} />
          </Button>
        )}
      </div>
    </article>
  )
}

/** Popup that connects one catalog item. */
function ConnectModal({ item, items, status, socialUrl, onClose, onDone }: {
  item: CatalogItem; items: Connector[]; status: CatalogStatus; socialUrl: string | null; onClose(): void; onDone(): void
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const granted = [...new Set(items.filter(c => c.kind === 'google').flatMap(c => asList(c.config.products)))]
  const signInGoogle = async (product: GoogleProduct) => {
    try { window.location.assign((await rpc.call<{ url: string }>('connectors.google.authUrl', { products: [...new Set([...granted, product])] })).url) }
    catch (err) { toast((err as Error).message, 'error') }
  }
  const title = t('connectors.catalog.connectTo', { name: item.name })
  const description = t(`connectors.catalog.desc_${item.id.replace('-', '_')}`)

  if (item.product) {
    return (
      <Modal
        open title={title} description={description} onClose={onClose}
        footer={<>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={() => void signInGoogle(item.product!)}>{t('connectors.google.signIn')}</Button>
        </>}
      >
        <div className="space-y-3 text-sm text-ink-400">
          <p>{t('connectors.catalog.googleIntro')}</p>
          {granted.length > 0 && <p>{t('connectors.catalog.googleKeeps', { list: granted.map(p => t(`connectors.google.product_${p}`)).join(', ') })}</p>}
          {item.product === 'gmail' && <p className="rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-700">{t('connectors.google.gmailNote')}</p>}
        </div>
      </Modal>
    )
  }
  return (
    <Modal open wide={item.section === 'mcp'} title={title} description={description} onClose={onClose}>
      {item.id === 'telegram' && <TelegramAddForm onDone={onDone} />}
      {item.id === 'social' && <SocialTokenForm onDone={onDone} url={socialUrl} />}
      {item.id === 'figma' && <FigmaTokenForm onDone={onDone} />}
      {item.id === 'zimbra' && <ZimbraLoginForm onDone={onDone} />}
      {item.id === 'jira' && <JiraConnectForm onDone={onDone} />}
      {item.id === 'github' && <GithubConnectForm onDone={onDone} appSlug={status.githubApp} />}
      {item.section === 'mcp' && (
        <div className="space-y-4">
          {item.preset && <p className="rounded-lg bg-cream-200 px-3 py-2 text-[13px] text-ink-300">{t('connectors.catalog.presetHint')}</p>}
          {item.id === 'figma-mcp' && <p className="rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-700">{t('connectors.catalog.figmaMcpNote')}</p>}
          <McpServerForm local={item.id === 'mcp-local'} preset={item.preset} onDone={onDone} onCancel={onClose} />
        </div>
      )}
    </Modal>
  )
}

export function ConnectorCatalog({ items, status, socialUrl, query, onOpen, onAdded, request, onRequestHandled }: {
  items: Connector[]; status: CatalogStatus; socialUrl: string | null
  /** Search text from the page toolbar. */
  query: string
  onOpen(connectorId: string): void; onAdded(): void
  /** Open this item's popup (the page's "Add" menu); the page clears it via `onRequestHandled`. */
  request?: ItemId | null; onRequestHandled?(): void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState<ItemId | null>(null)
  useEffect(() => {
    if (!request) return
    setOpen(request)
    onRequestHandled?.()
  }, [request, onRequestHandled])
  const all = catalog(t).filter(i => (i.id !== 'social' || status.social) && (i.id !== 'zimbra' || status.zimbra))
  const q = query.trim().toLowerCase()
  const shown = q ? all.filter(i => `${i.name} ${i.by} ${t(`connectors.catalog.desc_${i.id.replace('-', '_')}`)}`.toLowerCase().includes(q)) : all
  const unavailable = (i: CatalogItem): string | undefined =>
    i.product && !status.google ? t('connectors.catalog.googleOff')
      : i.id === 'mcp-local' && !status.sandbox ? t('connectors.catalog.needsSandbox')
        : i.id === 'github' && !status.sandbox ? t('repos.sandboxRequired')
        : undefined
  const current = all.find(i => i.id === open)

  const section = (key: CatalogItem['section'], title: string, hint: string) => {
    const list = shown.filter(i => i.section === key)
    if (!list.length) return null
    return (
      <section className="space-y-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold">
            {title}
            <span className="rounded-md bg-cream-200 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-ink-400">{list.length}</span>
          </h2>
          <p className="mt-0.5 text-[13px] text-ink-500">{hint}</p>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {list.map(i => (
            <CatalogCard key={i.id} item={i} connected={existing(i, items)} unavailable={unavailable(i)} onAdd={() => setOpen(i.id)} onOpen={onOpen} />
          ))}
        </div>
      </section>
    )
  }

  return (
    <div className="space-y-7">
      {shown.length === 0 && <p className="text-sm text-ink-500">{t('connectors.catalog.noResults')}</p>}
      {section('builtin', t('connectors.catalog.sectionBuiltin'), t('connectors.catalog.sectionBuiltinHint'))}
      {section('mcp', t('connectors.catalog.sectionMcp'), t('connectors.catalog.sectionMcpHint'))}
      {current && (
        <ConnectModal
          item={current} items={items} status={status} socialUrl={socialUrl}
          onClose={() => setOpen(null)} onDone={() => { setOpen(null); onAdded() }}
        />
      )}
    </div>
  )
}

export type { ItemId as CatalogItemId }
